import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { getAllQuestions, updateQuestion } from "../../application/services/questionService";
import { getInterviewConfigList, getInterviewConfigLookups, getInterviewTypesByCategory } from "../../application/services/interviewConfigService";
import useDebounce from "../hooks/useDebounce";
import SvgMapper from "../features/common/SvgMapper";
import CustomDropdown from "../InterviewConfigPage/CustomDropdown";
import "./Style.css";
import { renderDateTime } from "../utility/common";
import { HandThumbUpIcon, HandThumbDownIcon } from "@heroicons/react/24/outline";
import { HandThumbUpIcon as HandThumbUpSolid, HandThumbDownIcon as HandThumbDownSolid } from "@heroicons/react/24/solid";


const QuestionTabMain = () => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [difficulty, setDifficulty] = useState("all");
  const [interview, setInterview] = useState("all");
  const [page, setPage] = useState(1);
  const [categoryOptions, setCategoryOptions] = useState([
  { value: "all", label: "All Categories" },
  ]);

  const [interviewType, setInterviewType] = useState("all");
  const [interviewTypeOptions, setInterviewTypeOptions] = useState([
    { value: "all", label: "All Types" },
  ]);
  const [interviewTypeLoading, setInterviewTypeLoading] = useState(false);

  const hasFetchedLookups = useRef(false);
  
  useEffect(() => {
  if (hasFetchedLookups.current) return;
  hasFetchedLookups.current = true;

  const fetchLookups = async () => {
    try {
      const response = await getInterviewConfigLookups();

      const categories = response?.category?.data || [];

      const formatted = categories.map((item) => ({
        value: item.id,
        label: item.value,
      }));

      setCategoryOptions([
        { value: "all", label: "All Categories" },
        ...formatted,
      ]);
    } catch (err) {
      console.error("Failed to fetch category lookups:", err);
    }
  };

  fetchLookups();
}, []);

  useEffect(() => {
  const fetchInterviewTypes = async () => {
    if (!category || category === "all") {
      setInterviewTypeOptions([{ value: "all", label: "All Types" }]);
      setInterviewType("all");
      return;
    }

    try {
      setInterviewTypeLoading(true);

      const response = await getInterviewTypesByCategory(category);
      const types = response?.data || response || [];

      const formatted = types.map((item) => ({
        value: item.id,
        label: item.value,
      }));

      setInterviewTypeOptions([
        { value: "all", label: "All Types" },
        ...formatted,
      ]);

      setInterviewType("all");
    } catch (error) {
      console.error("Failed to fetch interview types:", error);
    } finally {
      setInterviewTypeLoading(false);
    }
  };

  fetchInterviewTypes();
}, [category]);

  const difficultyOptions = [
    { value: "all", label: "All Difficulties" },
    { value: "Beginner", label: "Beginner" },
    { value: "Intermediate", label: "Intermediate" },
    { value: "Advanced", label: "Advanced" },
  ];

  const [interviewOptions, setInterviewOptions] = useState([
    { value: "all", label: "All Interview" },
  ]);
  const [interviewPage, setInterviewPage] = useState(1);
  const [interviewHasMore, setInterviewHasMore] = useState(true);
  const [interviewLoading, setInterviewLoading] = useState(false);
  const interviewLoadingRef = useRef(false);
  const [pageSize] = useState(20);
  const [hasMore, setHasMore] = useState(true);

  const debouncedSearchQuery = useDebounce(searchQuery, 1000);
  const isInitialMount = useRef(true);
  const loadingRef = useRef(false);
  const scrollRef = useRef(null);

  const uniqueInterviewOptions = useMemo(() => {
    const seen = new Set();
    return interviewOptions.filter((opt) => {
      if (opt.value === "all") return true;
      if (seen.has(opt.label)) return false;
      seen.add(opt.label);
      return true;
    });
  }, [interviewOptions]);

  const handleLikeDislike = async (id, currentStatus, newStatus) => {
  const finalStatus = currentStatus === newStatus ? 0 : newStatus;

  try {
    await updateQuestion(id, {
      admin_rating: finalStatus,
    });

    setQuestions((prev) =>
      prev.map((q) =>
        q.question_id === id ? { ...q, admin_rating: finalStatus } : q
      )
    );
  } catch (err) {
    console.error(err);
  }
};

  const fetchQuestions = useCallback(
    async (pageNum = 1, filters = {}) => {
      if (loadingRef.current) return;

      try {
        loadingRef.current = true;
        setLoading(true);
        setError(null);

        const params = {
          page: pageNum,
          pageSize: pageSize,
          sort_by: "created_at",
          sort_order: "DESC",
          ...filters,
        };

        if (params.category_id === "all") {
          delete params.category_id;
        }
        if (params.difficulty === "all") delete params.difficulty;
        if (params.interview_config_id === "all" || !params.interview_config_id) {
          delete params.interview_config_id;
        }
        if (params.search && params.search.trim() === "") {
          delete params.search;
        }

        const response = await getAllQuestions(params);
        const newData = response?.data || response || [];

        if (pageNum === 1) {
          setQuestions(newData);
        } else {
          setQuestions((prev) => [...prev, ...newData]);
        }

        setHasMore(newData.length === pageSize);
        setPage(pageNum);
      } catch (err) {
        console.error("Error fetching questions:", err);
        setError(err?.message || "Failed to load questions");
        setQuestions([]);
      } finally {
        loadingRef.current = false;
        setLoading(false);
      }
    },
    [pageSize]
  );

  const fetchInterviews = useCallback(async (pageNum = 1, append = false) => {
    if (interviewLoadingRef.current) return;

    try {
      interviewLoadingRef.current = true;
      setInterviewLoading(true);
      
      const pageSizeLimit = 20;
      const response = await getInterviewConfigList(pageNum, pageSizeLimit, "created_at", "DESC");
      const interviews = response?.data || [];
      
      const newInterviewOptions = interviews.map((interview) => ({
        value: interview.id,
        label: interview.interview_name || "Unnamed Interview",
      }));
      
      if (append) {
        setInterviewOptions((prev) => {
          const existingLabels = new Set(prev.map((opt) => opt.label));
          const uniqueNewOptions = newInterviewOptions.filter(
            (opt) => !existingLabels.has(opt.label)
          );
          const combined = [...prev, ...uniqueNewOptions];
          const seen = new Set();
          return combined.filter((opt) => {
            if (opt.value === "all") return true;
            if (seen.has(opt.label)) return false;
            seen.add(opt.label);
            return true;
          });
        });
      } else {
        const uniqueMap = new Map();
        newInterviewOptions.forEach((opt) => {
          if (!uniqueMap.has(opt.label)) {
            uniqueMap.set(opt.label, opt);
          }
        });
        const uniqueOptions = Array.from(uniqueMap.values());
        
        setInterviewOptions([
          { value: "all", label: "All Interview" },
          ...uniqueOptions,
        ]);
      }
      
      setInterviewHasMore(newInterviewOptions.length === pageSizeLimit);
      setInterviewPage(pageNum);
    } catch (error) {
      console.error("Error fetching interviews:", error);
      setInterviewHasMore(false);
    } finally {
      interviewLoadingRef.current = false;
      setInterviewLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInterviews(1, false);
  }, [fetchInterviews]);

  const handleLoadMoreInterviews = useCallback(() => {
    if (interviewHasMore && !interviewLoading) {
      fetchInterviews(interviewPage + 1, true);
    }
  }, [interviewHasMore, interviewLoading, interviewPage, fetchInterviews]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      fetchQuestions(1, { category: "" });
    }
  }, [fetchQuestions]);

  useEffect(() => {
    if (isInitialMount.current) {
      return;
    }

    const filters = {};
    
    if (debouncedSearchQuery && debouncedSearchQuery.trim()) {
      filters.question = debouncedSearchQuery.trim();
    }
    
    if (category && category !== "all") {
      filters.category_id = category;
    }
    
    if (difficulty && difficulty !== "all") {
      filters.difficulty = difficulty.toLowerCase();
    }
    
    if (interview && interview !== "all") {
      filters.interview_config_id = interview;
    }

    if (interviewType && interviewType !== "all") {
      filters.interview_type_id = interviewType;
    }

    setPage(1);
    setHasMore(true);
    fetchQuestions(1, filters);
  }, [debouncedSearchQuery, category, difficulty, interviewType, interview, fetchQuestions]);

  const handleScroll = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;
    
    const { scrollTop, scrollHeight, clientHeight } = container;
    if (scrollTop + clientHeight >= scrollHeight - 100 && hasMore && !loading) {
      const filters = {};
      if (debouncedSearchQuery && debouncedSearchQuery.trim()) {
        filters.question = debouncedSearchQuery.trim();
      }
      if (category && category !== "all") {
        filters.category_id = category;
      }
      if (difficulty && difficulty !== "all") {
        filters.difficulty = difficulty.toLowerCase();
      }
      if (interview && interview !== "all") {
        filters.interview_config_id = interview;
      }
      if (interviewType && interviewType !== "all") {
        filters.interview_type_id = interviewType;
      }
      fetchQuestions(page + 1, filters);
    }
  }, [hasMore, loading, debouncedSearchQuery, category, difficulty, interviewType, interview, page, fetchQuestions]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    
    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  return (
    <div className="question-tab-container">
      <div className="question-tab-header">
        <nav className="question-tab-nav">
          <button className="question-tab-button">
            {SvgMapper.fileText("w-4 h-4")}
            <span>All Questions</span>
          </button>
        </nav>
      </div>
      <div className="question-tab-content">
        <div className="question-tab-filters question-tab-filters-sticky">
          <div className="question-tab-search-container">
            <div className="question-tab-search-icon">
              {SvgMapper.search("w-5 h-5")}
            </div>
            <input
              type="text"
              placeholder="Search questions, topics, or tags..."
              className="question-tab-search-input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <CustomDropdown
            value={category}
            options={categoryOptions}
            onChange={(e) => setCategory(e.target.value)}
          />
          <CustomDropdown
            value={interviewType}
            options={interviewTypeOptions}
            onChange={(e) => setInterviewType(e.target.value)}
            loading={interviewTypeLoading}
            disabled={!category || category === "all"}
          />
          <CustomDropdown
            value={difficulty}
            options={difficultyOptions}
            onChange={(e) => setDifficulty(e.target.value)}
          />
          <CustomDropdown
            value={interview}
            options={uniqueInterviewOptions}
            onChange={(e) => setInterview(e.target.value)}
            onLoadMore={handleLoadMoreInterviews}
            hasMore={interviewHasMore}
            loading={interviewLoading}
          />
        </div>
        <div 
          ref={scrollRef}
          className="question-tab-scroll-container custom-scrollbar"
        >

          {loading && questions.length === 0 ? (
            <div className="question-tab-loading">
              <p className="question-tab-loading-text">Loading questions...</p>
            </div>
          ) : error ? (
            <div className="question-tab-error">
              <p className="question-tab-error-text">Error: {error}</p>
            </div>
          ) : questions.length === 0 ? (
            <div className="question-tab-empty">
              <p className="question-tab-empty-text">No questions found.</p>
            </div>
          ) : (
            <>
              <div className="question-tab-questions-list">
                {questions.map((question) => {
                  const isLiked = question.admin_rating === 1;
                  const isDisliked = question.admin_rating === -1;
                return(
                  <div
                    key={question.question_id}
                    className="question-tab-question-card relative"
                  >
                    <div className="absolute top-3 right-3 flex items-center border rounded-md overflow-hidden bg-white shadow-sm">

                      <button
                        onClick={() =>
                          handleLikeDislike(question.question_id, question.admin_rating, 1)
                        }
                        className={`p-2 transition transform hover:scale-110 active:scale-95 ${
                          isLiked
                            ? "bg-blue-100 text-blue-600"
                            : "text-gray-500 hover:bg-gray-100"
                        }`}
                      >
                        {isLiked ? (
                          <HandThumbUpSolid className="w-5 h-5" />
                        ) : (
                          <HandThumbUpIcon className="w-5 h-5" />
                        )}
                      </button>

                      <div className="w-px bg-gray-300"></div>

                      <button
                        onClick={() =>
                          handleLikeDislike(question.question_id, question.admin_rating, -1)
                        }
                        className={`p-2 transition transform hover:scale-110 active:scale-95 ${
                          isDisliked
                            ? "bg-red-100 text-red-600"
                            : "text-gray-500 hover:bg-gray-100"
                        }`}
                      >
                        {isDisliked ? (
                          <HandThumbDownSolid className="w-5 h-5" />
                        ) : (
                          <HandThumbDownIcon className="w-5 h-5" />
                        )}
                      </button>
                   </div>
                   
                    <div className="question-tab-question-header">
                      <div className="question-tab-question-content">
                        <div className="question-tab-badges-container">
                          {question.interviewCategoryMapping?.value && (
                            <span className="question-tab-category-badge">
                              {question.interviewCategoryMapping.value}
                            </span>
                          )}
                          {question.section && (
                            <span className="question-tab-topic">
                              {question.section}
                            </span>
                          )}
                          {question.difficulty && (
                            <span
                              className={`question-tab-difficulty-badge ${
                                question.difficulty === "Beginner"
                                  ? "question-tab-difficulty-easy"
                                  : question.difficulty === "Intermediate"
                                  ? "question-tab-difficulty-medium"
                                  : "question-tab-difficulty-hard"
                              }`}
                            >
                              {question.difficulty}
                            </span>
                          )}
                          {question.topic && (
                            <span className="question-tab-topic">
                              {question.topic}
                            </span>
                          )}
                          {
                            question?.interviewConfigMapping?.interview_name && (
                              <span className="question-tab-interview-category-badge">{question.interviewConfigMapping.interview_name}</span>
                            )

                          }
                        </div>
                        <h3 className="question-tab-question-title">
                          {question.question_text || question.question || "N/A"}
                        </h3>
                        <div className="question-tab-meta-info">
                          {question.duration && (
                            <span className="question-tab-meta-item">
                              {SvgMapper.clock("w-4 h-4 mr-1")}
                              {question.duration} min
                            </span>
                          )}
                          {question.concept && (
                            <span className="question-tab-meta-item">
                              {SvgMapper.target("w-4 h-4 mr-1")}
                              {question.concept}
                            </span>
                          )}
                          {question.created_at && (
                            <span className="question-tab-meta-item">
                              {SvgMapper.calendar("w-4 h-4 mr-1")}
                              {renderDateTime(question.created_at)}
                            </span>
                          )}
                          {question.interviewTypeMapping?.value && (
                            <span className="question-tab-interview-type-badge">
                              {question.interviewTypeMapping.value}
                            </span>
                          )}
                        </div>
                        {question.tags && question.tags.length > 0 && (
                          <div className="question-tab-tags-container">
                            {question.tags.map((tag, index) => (
                              <span
                                key={index}
                                className="question-tab-tag"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    {question.expected_answer && (
                      <div className="question-tab-expected-answer">
                        <h4 className="question-tab-expected-answer-title">
                          Expected Answer:
                        </h4>
                        <p className="question-tab-expected-answer-text">
                          {question.expected_answer}
                        </p>
                      </div>
                    )}
                  </div>
                );
                })}
              </div>
              {loading && questions.length > 0 && (
                <div className="question-tab-load-more">
                  <p className="question-tab-load-more-text">Loading more questions...</p>
                </div>
              )}
              {!hasMore && questions.length > 0 && (
                <div className="question-tab-no-more">
                  <p className="question-tab-no-more-text">No more questions to load</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuestionTabMain;
